import React, { useMemo } from 'react';
import cn from 'classnames';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { Responsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { useResponsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { Element } from 'apps/sigma2-ui/src/components/ui/Element';
import { ElementPublicProps } from 'apps/sigma2-ui/src/components/ui/Element';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

type TypographyHTMLElementType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span';

// Keep in alphabetical order for easier glancing
export type TypographyTextColorVariant =
  | 'default'
  | 'inherit'
  | 'gray-400'
  | 'gray-500'
  | 'gray-600'
  | 'gray-700'
  | 'gray-800'
  | 'gray-900'
  | 'gray-1000'
  | 'green-800'
  | 'red-500'
  | 'red-600'
  | 'red-700'
  | 'red-800'
  | 'royal-200'
  | 'royal-600'
  | 'royal-700'
  | 'royal-800'
  | 'royal-900'
  | 'secondary'
  | 'violet-100'
  | 'teal-600'
  | 'teal-700'
  | 'violet-800'
  | 'violet-900'
  | 'white'
  | 'yellow-700'
  | 'yellow-500'
  | 'green-600'
  | 'orange-500'
  | 'indigo-900'
  | 'indigo-1000'
  | 'purple-900';
export interface TypographyProps extends ElementPublicProps {
  /**
   * The variant of the typography. This is used to set the font size, weight, and color. It's based on the Sigma Design system.
   * @url https://www.figma.com/file/hTh2CtFhxWqMBrA40QbxI9/Sigma-Design-System?type=design&node-id=1610-27&mode=design&t=i3WmPQnCdB7LIc8R-0
   */
  variant: Responsive<TypographyVariant>;
  /**
   * The variant of the text color. This allows you to override the default text color of the variant. It's limited to the colors that we use in our design system.
   */
  textColorVariant?: TypographyTextColorVariant;
  /**
   * The translation of the text. This is used to localize the text.
   * @note for things we don't have control over like a displayName, use the 'dangerousUserInput' translation.id
   * @see libs/end-to-end/src/language.ts
   */
  translatedContent: TranslatedContent;
  /**
   * Used for overriding the default element type. This is useful for when you want to use a different element type to match the semantics of the content.
   * @example <Typography variant="headline-1" as="h2" />
   */
  as?: TypographyHTMLElementType;
  /**
   * Allows you to override the className of the variant as an escape hatch. This is useful for when you want to use a different color. This is a last resort and should be avoided if possible.
   * @example <Typography variant="headline-1" dangerouslyOverrideClassNameFn={replaceTextColor(someSpecialColor)} />
   * @see replaceTextColor
   */
  dangerouslyOverrideClassNameFn?: (variantClassName: string) => string;
  underline?: boolean;
  truncate?: boolean;
}

export type TypographyVariant =
  | 'headline-1'
  | 'headline-2'
  | 'headline-3'
  | 'headline-4'
  | 'headline-5'
  | 'headline-6'
  | 'headline-7'
  | 'headline-1-alt'
  | 'headline-2-alt'
  | 'headline-3-alt'
  | 'headline-4-alt'
  | 'headline-5-alt'
  | 'headline-6-alt'
  | 'headline-7-alt'
  | 'headline-1-alt-underline'
  | 'headline-2-alt-underline'
  | 'headline-3-alt-underline'
  | 'headline-4-alt-underline'
  | 'headline-5-alt-underline'
  | 'headline-6-alt-underline'
  | 'headline-7-alt-underline'
  | 'body-1'
  | 'body-2'
  | 'body-2-extralight'
  | 'body-3'
  | 'body-4'
  | 'body-5'
  | 'body-6'
  | 'body-6-bold'
  | 'body-6-medium'
  | 'body-7'
  | 'body-7-bold'
  | 'body-7-medium'
  | 'small-label'
  | 'small-label-bold'
  | 'small-label-alt'
  | 'link'
  | 'nav-body-7'
  | 'nav-body-7-alt';

/**
 * @note these classes should NOT have any text colors in them.
 * @see defaultTextColorToClassName for default text color classes
 */
export const variantToClassNameWithoutTextColor: Record<
  TypographyVariant,
  string
> = {
  'headline-1': 'text-4xl font-serif font-medium leading-normal',
  'headline-2': 'text-3xl font-serif font-medium leading-normal',
  'headline-3': 'text-2xl font-serif font-medium leading-normal',
  'headline-4': 'text-xl font-serif font-medium leading-normal',
  'headline-5': 'text-lg font-serif font-medium leading-normal',
  'headline-6': 'text-sm font-serif font-semibold leading-normal',
  'headline-7': 'text-xs font-serif font-semibold leading-normal',
  'headline-1-alt': 'text-4xl font-sans font-bold leading-normal',
  'headline-2-alt': 'text-3xl font-sans font-bold leading-normal',
  'headline-3-alt': 'text-2xl font-sans font-bold leading-normal',
  'headline-4-alt': 'text-xl font-sans font-bold leading-normal',
  'headline-5-alt': 'text-lg font-sans font-bold leading-normal',
  'headline-6-alt': 'text-sm font-sans font-bold leading-normal',
  'headline-7-alt': 'text-xs font-sans font-bold leading-normal',
  'headline-1-alt-underline':
    'text-4xl font-sans font-bold leading-normal underline',
  'headline-2-alt-underline':
    'text-3xl font-sans font-bold leading-normal underline',
  'headline-3-alt-underline':
    'text-2xl font-sans font-bold leading-normal underline',
  'headline-4-alt-underline':
    'text-xl font-sans font-bold leading-normal underline',
  'headline-5-alt-underline':
    'text-lg font-sans font-bold leading-normal underline',
  'headline-6-alt-underline':
    'text-sm font-sans font-bold leading-normal underline',
  'headline-7-alt-underline':
    'text-xs font-sans font-bold leading-normal underline',
  'body-1': 'text-4xl font-sans font-normal leading-normal',
  'body-2': 'text-3xl font-sans font-normal leading-normal',
  'body-2-extralight': 'text-3xl font-sans font-extralight leading-normal',
  'body-3': 'text-2xl font-sans font-normal leading-normal',
  'body-4': 'text-xl font-sans font-normal leading-normal',
  'body-5': 'text-lg font-sans font-normal leading-normal',
  'body-6': 'text-sm font-sans font-normal leading-normal',
  'body-6-bold': 'text-sm font-sans font-bold leading-normal',
  'body-6-medium': 'text-sm font-sans font-medium leading-normal',
  'body-7': 'text-xs font-sans font-normal leading-normal',
  'body-7-bold': 'text-xs font-sans font-bold leading-normal',
  'body-7-medium': 'text-xs font-sans font-medium leading-normal',
  'small-label': 'text-xxs font-normal leading-normal',
  'small-label-bold': 'text-xxs font-bold leading-normal',
  'small-label-alt': 'text-xxs font-bold leading-normal',
  link: 'text-sm font-sans font-bold',
  'nav-body-7': 'text-xs font-sans font-medium leading-normal',
  'nav-body-7-alt': 'text-xs font-sans font-bold leading-normal',
};

/**
 * We split this out from the other text color variants because we want to make sure that we don't specify color more than once, which would be a footgun with Tailwind
 */
const defaultTextColorVariantToClassName: Record<
  TypographyVariant,
  `text-${string}`
> = {
  'headline-1': 'text-gray-900',
  'headline-2': 'text-gray-900',
  'headline-3': 'text-gray-900',
  'headline-4': 'text-gray-900',
  'headline-5': 'text-gray-900',
  'headline-6': 'text-gray-900',
  'headline-7': 'text-gray-900',
  'headline-1-alt': 'text-gray-900',
  'headline-2-alt': 'text-gray-900',
  'headline-3-alt': 'text-gray-900',
  'headline-4-alt': 'text-gray-900',
  'headline-5-alt': 'text-gray-900',
  'headline-6-alt': 'text-gray-900',
  'headline-7-alt': 'text-gray-900',
  'headline-1-alt-underline': 'text-gray-900',
  'headline-2-alt-underline': 'text-gray-900',
  'headline-3-alt-underline': 'text-gray-900',
  'headline-4-alt-underline': 'text-gray-900',
  'headline-5-alt-underline': 'text-gray-900',
  'headline-6-alt-underline': 'text-gray-900',
  'headline-7-alt-underline': 'text-gray-900',
  'body-1': 'text-gray-600',
  'body-2': 'text-gray-600',
  'body-2-extralight': 'text-gray-600',
  'body-3': 'text-gray-600',
  'body-4': 'text-gray-600',
  'body-5': 'text-gray-600',
  'body-6': 'text-gray-600',
  'body-6-bold': 'text-gray-600',
  'body-6-medium': 'text-gray-600',
  'body-7': 'text-gray-600',
  'body-7-bold': 'text-gray-600',
  'body-7-medium': 'text-gray-600',
  'small-label': 'text-gray-600',
  'small-label-bold': 'text-gray-600',
  'small-label-alt': 'text-gray-600',
  link: 'text-royal-600',
  'nav-body-7': 'text-gray-600',
  'nav-body-7-alt': 'text-gray-700',
};

const textColorVariantToClassName: Record<
  Exclude<TypographyTextColorVariant, 'default'>,
  `text-${string}`
> = {
  inherit: 'text-inherit',
  'gray-400': 'text-gray-400',
  'gray-500': 'text-gray-500',
  'gray-600': 'text-gray-600',
  'gray-700': 'text-gray-700',
  'gray-800': 'text-gray-800',
  'gray-900': 'text-gray-900',
  'gray-1000': 'text-gray-1000',
  'green-800': 'text-green-800',
  'red-500': 'text-red-500',
  'red-600': 'text-red-600',
  'red-700': 'text-red-700',
  'red-800': 'text-red-800',
  'royal-200': 'text-royal-200',
  'royal-600': 'text-royal-600',
  'royal-700': 'text-royal-700',
  'royal-800': 'text-royal-800',
  'royal-900': 'text-royal-900',
  secondary: 'text-secondary',
  'violet-100': 'text-violet-100',
  'teal-600': 'text-teal-600',
  'teal-700': 'text-teal-700',
  'violet-800': 'text-violet-800',
  'violet-900': 'text-violet-900',
  white: 'text-white',
  'yellow-700': 'text-yellow-700',
  'green-600': 'text-green-600',
  'yellow-500': 'text-yellow-500',
  'orange-500': 'text-orange-500',
  'indigo-900': 'text-indigo-900',
  'indigo-1000': 'text-indigo-1000',
  'purple-900': 'text-purple-900',
};

/**
 * Helper for replacing the text color of a className.
 * @example <Typography variant="headline-1" dangerouslyOverrideClassNameFn={replaceTextColor(someSpecialColor)} />
 * @deprecated use the `textColorVariant` prop instead. Add a new color if needed
 */
export const replaceTextColor =
  (textColorClassName: string) =>
  (variantClassName: string): string => {
    return variantClassName.replace(
      /(text-[a-z]*-\d{3,4}|text-white|text-black|text-primary|text-secondary)/,
      textColorClassName,
    );
  };

const getDefaultElementType = (
  variant: TypographyVariant,
): TypographyHTMLElementType => {
  switch (variant) {
    case 'headline-1':
    case 'headline-1-alt':
    case 'headline-1-alt-underline':
      return 'h1';

    case 'headline-2':
    case 'headline-2-alt':
    case 'headline-2-alt-underline':
      return 'h2';

    case 'headline-3':
    case 'headline-3-alt':
    case 'headline-3-alt-underline':
      return 'h3';

    case 'headline-4':
    case 'headline-4-alt':
    case 'headline-4-alt-underline':
      return 'h4';

    case 'headline-5':
    case 'headline-5-alt':
    case 'headline-5-alt-underline':
      return 'h5';

    case 'headline-6':
    case 'headline-6-alt':
    case 'headline-6-alt-underline':
    case 'headline-7':
    case 'headline-7-alt':
    case 'headline-7-alt-underline':
      return 'h6';

    case 'body-1':
    case 'body-2':
    case 'body-2-extralight':
    case 'body-3':
    case 'body-4':
    case 'body-5':
    case 'body-6':
    case 'body-6-bold':
    case 'body-6-medium':
    case 'body-7':
    case 'body-7-bold':
    case 'body-7-medium':
    case 'nav-body-7':
    case 'nav-body-7-alt':
      return 'p';

    case 'small-label':
    case 'small-label-bold':
    case 'small-label-alt':
    case 'link':
      return 'span';
  }
};

/**
 * The one stop shop for all typography in the app. This component is the only place where we should be setting font sizes, weights, and colors that match the Sigma Design system.
 * It forces us to localize our text, uses the same language as our designers/design system, and it easier to refactor later on since we can easily search for all instances of a specific variant, prevents us from having
 * to override props or classNames, and has less footguns like the way tailwind can't have two props with similar prefixes, like text-sm text-lg will choose based on which is first in the autogenerated stylesheet, not the ordering in the classname.
 *
 * @note If you want something like some margin or padding, wrap in a <Spacing> or div. If you want to make it a different color, it's not a valid Body element anymore.
 * The main idea behind this logic is to prevent having to override things all of the time, which makes it more declarative and easier to refactor from one place. Also less footguns like the way tailwind can't have two props with similar prefixes, like text-sm text-lg will choose based on which is first in the autogenerated stylesheet, not the ordering in the classname, etc
 *
 * @url https://www.figma.com/file/hTh2CtFhxWqMBrA40QbxI9/Sigma-Design-System?type=design&node-id=258-143&mode=design&t=i3WmPQnCdB7LIc8R-0
 *
 * @example <Typography variant="headline-1" translatedContent={language.id()} />
 */

export const S2Typography = ({
  variant,
  textColorVariant = 'default',
  translatedContent,
  as,
  dangerouslyOverrideClassNameFn = (variantClassName) => variantClassName,
  truncate,
  flex,
  underline,
}: TypographyProps): React.ReactElement | null => {
  const resolvedVariant = useResponsive(variant);

  const elementType = as || getDefaultElementType(resolvedVariant);

  const textColorClassName: string = useMemo(() => {
    if (textColorVariant !== 'default') {
      return textColorVariantToClassName[textColorVariant];
    }

    return defaultTextColorVariantToClassName[resolvedVariant];
  }, [textColorVariant, resolvedVariant]);

  return (
    <Element
      as={elementType}
      extraClassNames={dangerouslyOverrideClassNameFn(
        cn(
          variantToClassNameWithoutTextColor[resolvedVariant],
          textColorClassName,
          underline && 'underline',
        ),
      )}
      truncate={truncate}
      flex={flex}
    >
      {convertTranslatedContentToReactNode(translatedContent)}
    </Element>
  );
};
